// Service/application version inventory + version-fetch engine — ported from the standalone `web/internals`
// app so its two screens (Service Versions, Versions Across Environments) can live as Headlight-only views in
// this config app. Only adaptation from the original: `currentEnvironmentKey` reads the browser host (this
// app's REST client base is relative). Fetches the public `/Version` + `/1.0/ServiceVersion/<hash>` endpoints
// cross-subdomain via `fetch` — a version that a CORS/host denies simply shows as unknown ('???').
const libPictProvider = require('pict-provider');

const MONOREPO_URL = 'https://github.com/paviasystems/headlight';
const SERVICE_VERSION_ROUTE_PREFIX = '/1.0/ServiceVersion';
// Reported by a service whose version file could not be read; never a real version.
const UNKNOWN_VERSION = '--';
// A hung host would otherwise hold the whole versions view until the gateway's 60s connect timeout.
const VERSION_FETCH_TIMEOUT_MS = 5000;

// -------------------------------------------------------------------------------------------------
// TEMPORARY: paths these services answered on before the ServiceVersion namespace existed, for
// environments not yet deployed with it. Applications need no equivalent — they serve /Version at the
// same URL in both paradigms.
//
// To remove once staging and production are cut over: delete this map, `fetchLegacyVersion`, and the
// one call to it in `fetchVersionForService`.
// -------------------------------------------------------------------------------------------------
const LEGACY_VERSION_PATHS =
{
	FinancialService: '/1.0/Finance/Version',
	MaterialsService: '/1.0/LIMS/Version',
	PermissionService: '/1.0/Permissions/Version',
	PDFRasterizationService: '/1.0/Rasterize/Version',
	UserManagementService: '/1.0/UM/Version',
};

/**
 * How a version is fetched: back-end services answer on a shared path, applications on their own subdomain.
 *
 * @typedef {'Application' | 'Micro-Service'} ServiceType
 */

/**
 * How an entry is grouped for display, mirroring the Rancher stacks plus the legacy application set.
 *
 * @typedef {'Service' | 'Application' | 'Legacy' | 'MissionControl' | 'Ultravisor'} ServiceGroupKey
 */

/**
 * @typedef {Object} ServiceCatalogEntry
 * @property {string} Name - what we call the service or application.
 * @property {ServiceType} Type
 * @property {ServiceGroupKey} Group
 * @property {string} [ServiceHash] - Micro-Service only; the hash the service publishes its version under, matching its fable `Product`.
 * @property {string} [ApplicationSubdomain] - Application only; the subdomain the application is served from.
 * @property {string} [VersionHost] - Micro-Service only; the subdomain that answers its ServiceVersion route, when not `api`.
 * @property {string} [RepoBaseURL] - defaults to the monorepo.
 */

/**
 * @typedef {Object} EnvironmentInfo
 * @property {string} Key - subdomain segment used to build URLs; empty for production.
 * @property {string} Label
 */

/**
 * @typedef {Object} ServiceGroupInfo
 * @property {ServiceGroupKey} Key
 * @property {string} Label
 */

/**
 * The single inventory of what the internals application reports versions for.
 *
 * Back-end services publish under `/1.0/ServiceVersion/<ServiceHash>` on the shared API host (the
 * Ultravisor stack on its own hub host, which answers for every connected beacon), since
 * they have no DNS of their own; applications serve `/Version` from their own subdomain.
 */
class ServiceCatalog extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('../Application-Web.js').INTERNALSPict} */
		this.pict;

		/** @type {EnvironmentInfo[]} */
		this.environments =
		[
			{ Key: 'qa', Label: 'QA' },
			{ Key: 'stg', Label: 'STG' },
			{ Key: '', Label: 'PROD' },
		];

		/** @type {ServiceGroupInfo[]} */
		this.groups =
		[
			{ Key: 'Service', Label: 'Back-End Services' },
			{ Key: 'Application', Label: 'Applications' },
			{ Key: 'Legacy', Label: 'Legacy Applications' },
			{ Key: 'MissionControl', Label: 'Mission Control' },
			{ Key: 'Ultravisor', Label: 'Ultravisor Stack' },
		];

		/** @type {ServiceCatalogEntry[]} */
		this.services =
		[
			{ Name: 'API Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'HLServer' },
			{ Name: 'Approvals Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'ApprovalsService' },
			{ Name: 'Asset Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'AssetService' },
			{ Name: 'Authentication Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'HLAuth' },
			{ Name: 'Config Sync Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'ConfigSyncService' },
			{ Name: 'Configuration Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'ConfigService' },
			{ Name: 'Distilling Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'DistillingService' },
			{ Name: 'Document Packaging Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'FormPackaging' },
			{ Name: 'Email Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'EmailService' },
			{ Name: 'Entity Bundles Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'EntityBundlesService' },
			{ Name: 'Finance Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'FinancialService' },
			{ Name: 'Forms Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'FormsService' },
			{ Name: 'Materials Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'MaterialsService' },
			{ Name: 'Membership Sync Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'MembershipSync' },
			{ Name: 'Permissions Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'PermissionService' },
			{ Name: 'Platform Data Ops Service / Data App', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'PlatformDataOperations' },
			{ Name: 'Public Data Lake Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'PublicDataLakeAPI' },
			{ Name: 'Rasterization Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'PDFRasterizationService' },
			{ Name: 'Report Definitions', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'ReportDefinitions', RepoBaseURL: 'https://github.com/paviasystems/report_definitions' },
			{ Name: 'Report Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'ReportService' },
			{ Name: 'Sync Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'SyncService' },
			{ Name: 'User Mgmt Service', Group: 'Service', Type: 'Micro-Service', ServiceHash: 'UserManagementService' },

			{ Name: 'Data Service', Group: 'MissionControl', Type: 'Micro-Service', ServiceHash: 'MissionControlDataService' },
			{ Name: 'Image Metadata Parser', Group: 'MissionControl', Type: 'Micro-Service', ServiceHash: 'HeadLight-MissionControl-ImageMetadataParser' },
			{ Name: 'Job Pusher', Group: 'MissionControl', Type: 'Micro-Service', ServiceHash: 'HeadLight-MissionControl-JobPusher' },
			{ Name: 'Librarian', Group: 'MissionControl', Type: 'Micro-Service', ServiceHash: 'HeadLight-MissionControl-Librarian' },

			// ServiceHash is each image's `product-name` in .github/workflows/services_headlight-*.yaml.
			{ Name: 'Ultravisor Hub', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'UltravisorAuthBeacon' },
			{ Name: 'Comprehension Loader Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'ComprehensionLoaderBeacon' },
			{ Name: 'MCP Data Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'MCPDataBeacon' },
			{ Name: 'Meadow (API) Data Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'MeadowDatabeacon' },
			{ Name: 'MySQL Data Beacons', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'MySQLDatabeacon' },
			{ Name: 'Notification Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'NotificationBeacon' },
			{ Name: 'Postgres (Private Lake) Data Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'PostgresDatabeacon' },
			{ Name: 'Private Lake Ingest Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'PrivateLakeIngestBeacon' },
			{ Name: 'Projection Refresh Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'ProjectionRefreshBeacon' },
			{ Name: 'Type Seeding Beacon', Group: 'Ultravisor', Type: 'Micro-Service', VersionHost: 'ultravisor', ServiceHash: 'TypeSeedingBeacon' },

			{ Name: 'Asset Management (v2)', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'assets2' },
			{ Name: 'Assets', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'assets' },
			{ Name: 'Bridge Inspection', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'bridge' },
			{ Name: 'Bundles Explorer', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'bundles' },
			{ Name: 'Contract Admin', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'contractadmin' },
			{ Name: 'Dashboards', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'dashboards' },
			{ Name: 'Documentation', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'docs' },
			{ Name: 'Form Editor', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'forms' },
			{ Name: 'Home', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'home' },
			{ Name: 'Internals', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'internals' },
			{ Name: 'LIMS', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'lims' },
			{ Name: 'Links', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'links' },
			{ Name: 'Materials Sample Tracking', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'materials', RepoBaseURL: 'https://github.com/paviasystems/materials-sampletracking' },
			{ Name: 'Payment Data', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'paymentdata' },
			{ Name: 'Platform Configuration', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'config' },
			{ Name: 'Profile', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'profile' },
			{ Name: 'Sample Plan', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'sampleplan' },
			{ Name: 'Smart Forms', Group: 'Application', Type: 'Application', ApplicationSubdomain: 'smartforms' },

			{ Name: 'Contracts', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'contracts' },
			{ Name: 'Customers', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'customers' },
			{ Name: 'Dashboard', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'dashboard' },
			{ Name: 'Fieldbook', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'fieldbook' },
			{ Name: 'Portal', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'portal' },
			{ Name: 'Projects', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'projects' },
			{ Name: 'Tracker', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'tracker' },
			{ Name: 'Vault', Group: 'Legacy', Type: 'Application', ApplicationSubdomain: 'vault' },
		];
	}

	/**
	 * The environment the application is currently pointed at.
	 *
	 * @return {string} The environment subdomain segment; empty string for production.
	 */
	get currentEnvironmentKey()
	{
		// The config app's REST client base is RELATIVE ('/1.0/') — unlike the standalone internals app whose
		// client points at the absolute API host — so it carries no environment segment. Detect the environment
		// from the browser host instead: config.<env>.headlight.com (prod = config.headlight.com; local → qa).
		const tmpSegments = String((typeof window !== 'undefined' && window.location && window.location.hostname) || '').split('.');
		switch (tmpSegments[1])
		{
			case 'headlight':
			case 'paviasystems':
				return '';
			case 'qa':
			case 'stg':
			case 'demo':
				return tmpSegments[1];
			default:
				return 'qa';
		}
	}

	/**
	 * The entries belonging to a display group, in catalog order.
	 *
	 * @param {ServiceGroupKey} pGroupKey - The group to select.
	 *
	 * @return {ServiceCatalogEntry[]} The matching entries.
	 */
	servicesInGroup(pGroupKey)
	{
		return this.services.filter((pService) => pService.Group === pGroupKey);
	}

	/**
	 * Build the absolute URL a service or application reports its version at.
	 *
	 * @param {ServiceCatalogEntry} pService - The catalog entry.
	 * @param {string} pEnvironmentKey - The environment subdomain segment; empty string for production.
	 *
	 * @return {string} The absolute URL, or an empty string when the entry is not fetchable.
	 */
	buildVersionURL(pService, pEnvironmentKey)
	{
		if (pService.Type === 'Application')
		{
			if (!pService.ApplicationSubdomain)
			{
				return '';
			}
			return `https://${pService.ApplicationSubdomain}.${pEnvironmentKey}.headlight.com/Version`.replace('..', '.');
		}
		if (!pService.ServiceHash)
		{
			return '';
		}
		return `https://${pService.VersionHost || 'api'}.${pEnvironmentKey}.headlight.com${SERVICE_VERSION_ROUTE_PREFIX}/${pService.ServiceHash}`.replace('..', '.');
	}

	/**
	 * Build the repository link for a version.
	 *
	 * @param {ServiceCatalogEntry} pService - The catalog entry.
	 * @param {string} pHashOrTag - The reported version.
	 *
	 * @return {string} The commit URL, or an empty string when there is nothing to link to.
	 */
	buildHeadCommitURL(pService, pHashOrTag)
	{
		if (!pService || !pHashOrTag || pHashOrTag === UNKNOWN_VERSION)
		{
			return '';
		}
		return `${pService.RepoBaseURL || MONOREPO_URL}/commits/${pHashOrTag}`;
	}

	/**
	 * Fetch a reported version.
	 *
	 * @param {string} pURL - The absolute version URL.
	 *
	 * @return {Promise<{version:string, packageVersion:string?}?>} The reported info, or null when unreadable.
	 */
	async fetchVersion(pURL)
	{
		if (!pURL)
		{
			return null;
		}
		try
		{
			const tmpResponse = await fetch(pURL, { signal: AbortSignal.timeout(VERSION_FETCH_TIMEOUT_MS) });
			if (!tmpResponse.ok)
			{
				this.pict.log.warn(`${tmpResponse.status} failed to fetch version from ${pURL}`);
				return null;
			}
			const tmpData = await tmpResponse.json();
			if (tmpData.version == null) { return null; }
			// Services report { version, packageVersion, serviceHash }; apps report just { version }. Keep the
			// deployed `version` (displayed + linked to the commit) plus `packageVersion` (the entry's own
			// package.json semver — services only; null for apps or a service reporting the '--' sentinel).
			return { version: tmpData.version, packageVersion: (tmpData.packageVersion != null) ? tmpData.packageVersion : null };
		}
		catch (pError)
		{
			this.pict.log.warn(`failed to fetch version from ${pURL}: ${pError.message}`, pError);
			return null;
		}
	}

	/**
	 * Whether a reported value is an actual version, as opposed to a service answering without one.
	 *
	 * @param {string?} pVersion - The reported version.
	 *
	 * @return {boolean} true if the value is a real version.
	 */
	isRealVersion(pVersion)
	{
		return !!pVersion && pVersion !== UNKNOWN_VERSION;
	}

	/**
	 * Fetch the version a service or application reports.
	 *
	 * @param {ServiceCatalogEntry} pService - The catalog entry.
	 * @param {string} pEnvironmentKey - The environment subdomain segment; empty string for production.
	 *
	 * @return {Promise<string?>} The reported version, or null when it could not be read.
	 */
	/**
	 * Fetch the full version info a service/app reports: { version, packageVersion }. `version` = deployed
	 * monorepo/release version; `packageVersion` = the entry's own package.json semver (services only).
	 * @param {ServiceCatalogEntry} pService @param {string} pEnvironmentKey
	 * @return {Promise<{version:string, packageVersion:string?}?>} The info, or null when nothing was read.
	 */
	async fetchVersionInfoForService(pService, pEnvironmentKey)
	{
		const tmpInfo = await this.fetchVersion(this.buildVersionURL(pService, pEnvironmentKey));
		if (tmpInfo !== null)
		{
			return tmpInfo;
		}
		return this.fetchLegacyVersion(pService, pEnvironmentKey);
	}

	async fetchVersionForService(pService, pEnvironmentKey)
	{
		const tmpInfo = await this.fetchVersionInfoForService(pService, pEnvironmentKey);
		return tmpInfo ? tmpInfo.version : null;
	}

	/**
	 * Fetch a version from the path a service answered on before the ServiceVersion namespace existed.
	 *
	 * Remove alongside `LEGACY_VERSION_PATHS`.
	 *
	 * @param {ServiceCatalogEntry} pService - The catalog entry.
	 * @param {string} pEnvironmentKey - The environment subdomain segment; empty string for production.
	 *
	 * @return {Promise<string?>} The reported version, or null when there is no legacy path or it could not be read.
	 */
	async fetchLegacyVersion(pService, pEnvironmentKey)
	{
		const tmpLegacyPath = (pService.Type === 'Micro-Service') ? LEGACY_VERSION_PATHS[pService.ServiceHash] : null;
		if (!tmpLegacyPath)
		{
			return null;
		}
		return this.fetchVersion(`https://api.${pEnvironmentKey}.headlight.com${tmpLegacyPath}`.replace('..', '.'));
	}
}

module.exports = ServiceCatalog;

module.exports.default_configuration = (
	{
		Name: 'InternalsServiceCatalog',
	});
